/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { RemotePartyPlaceholder, RoomTopBar, SelfViewPip } from '@/components/telemedicine/room/RoomParts';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import {
  getAppointment, getVideoRoom, startVisit, endVisit,
} from '@/lib/telemedicine';
import api from '@/lib/api';
import PrescriptionDialog from '@/components/telemedicine/PrescriptionDialog';
import LiveTranscriptPanel from '@/components/telemedicine/LiveTranscriptPanel';
import WaitingRoom from '@/components/telemedicine/WaitingRoom';
import NotesPanel from '@/components/telemedicine/NotesPanel';
import { useWebRTC } from '@/lib/useWebRTC';
import { VideoControlsBar } from '@/components/telemedicine/VideoControlsBar';

/**
 * VideoRoomPage - FaceTime-style full screen visit page.
 *
 * Refactor note (Phase 8): the sub-components `WaitingRoom` and `NotesPanel`
 * were extracted to their own files under `components/telemedicine/*` to keep
 * this file focused on the actual video call flow and WebRTC wiring.
 */

const VideoRoomPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { appt_id } = useParams();
  const navigate = useNavigate();

  const [appt, setAppt] = useState(null);
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);

  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [notesOpen, setNotesOpen] = useState(false);
  const [rxOpen, setRxOpen] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [endLoading, setEndLoading] = useState(false);
  const [startLoading, setStartLoading] = useState(false);

  // WebRTC P2P (zero-cost) — connects via WebSocket signaling + STUN/TURN
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [wsToken, setWsToken] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/auth/ws-token');
        if (!cancelled) setWsToken(res.data?.token || '');
      } catch (e) {
        // Sprint 9I: legacy localStorage fallback removed for security.
        // If /auth/ws-token fails, the user is unauthenticated → leave token empty.
        if (!cancelled) setWsToken('');
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const rtc = useWebRTC({
    appointmentId: appt_id,
    autoConnect: appt?.status === 'in_progress' && !!wsToken,
    camOn,
    micOn,
    backendUrl,
    token: wsToken,
  });

  const isDoctor = user?.id === appt?.doctor_id;
  const isPatient = user?.id === appt?.patient_id;

  const load = React.useCallback(async () => {
    try {
      const [ap, rm] = await Promise.all([
        getAppointment(appt_id),
        getVideoRoom(appt_id).catch((err) => {
          if (process.env.NODE_ENV !== 'production') console.debug('[VideoRoom] getVideoRoom failed', err);
          return null;
        }),
      ]);
      setAppt(ap.appointment);
      setDoctorInfo(ap.doctor);
      setPatientInfo(ap.patient);
      setRoom(rm);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('genericError'));
      navigate('/telemedicine');
    } finally {
      setLoading(false);
    }
  }, [appt_id, navigate, t]);
  useEffect(() => { load(); }, [load]);

  const beginVisit = async () => {
    setStartLoading(true);
    try {
      await startVisit(appt_id);
      await load();
      toast.success(t('visitStarted'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('genericError'));
    } finally { setStartLoading(false); }
  };

  const hangUp = async () => {
    if (isDoctor) {
      setEndLoading(true);
      try {
        await endVisit(appt_id);
        toast.success(t('visitEnded'));
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.debug('[VideoRoom] endVisit failed', e);
      } finally { setEndLoading(false); }
    }
    navigate('/telemedicine');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
          <p className="text-sm text-muted-foreground mt-2">{t('processing')}</p>
        </div>
      </div>
    );
  }

  // Waiting room: not yet in join window
  if (room && !room.can_join) {
    return <WaitingRoom appt={appt} room={room} onCheckAgain={load} />;
  }

  const otherParty = isDoctor ? patientInfo : doctorInfo;
  const otherName = isDoctor ? appt?.patient_name : `Dr. ${appt?.doctor_name}`;
  const roomActive = appt?.status === 'in_progress';
  const signalingLabel = rtc.connecting ? 'Connessione in corso…' : (t('videoWaiting') || 'In attesa del partecipante');

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col overflow-hidden z-50" data-testid="video-room-page">
      {/* Main "remote party" video area */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        {/* Placeholder remote video background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-950 to-black" />
        <div className="absolute inset-0 bg-grain opacity-20" />

        {/* WebRTC remote video (P2P, zero cost) */}
        <div
          className={cn(
            'absolute inset-0 bg-black transition-opacity duration-500',
            rtc.remoteConnected ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
          data-testid="webrtc-remote-video"
        >
          <video
            ref={rtc.remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
        </div>

        {!rtc.remoteConnected && (
          <RemotePartyPlaceholder
            otherParty={otherParty} otherName={otherName} roomActive={roomActive}
            isDoctor={isDoctor} isPatient={isPatient} onBeginVisit={beginVisit}
            startLoading={startLoading} rtc={rtc} signalingLabel={signalingLabel}
          />
        )}

        {/* WebRTC connecting indicator */}
        {rtc.connecting && !rtc.remoteConnected && (
          <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
            <div className="px-4 py-2 rounded-full glass-strong text-white text-sm animate-pulse" data-testid="rtc-connecting">
              Attivazione fotocamera e microfono…
            </div>
          </div>
        )}
        {rtc.connected && !rtc.remoteConnected && rtc.peerName && (
          <div className="absolute top-20 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
            <div className="px-4 py-2 rounded-full glass-strong text-white text-sm">
              {rtc.peerName} è entrato — chiamata in corso…
            </div>
          </div>
        )}

        <RoomTopBar />
        <SelfViewPip rtc={rtc} camOn={camOn} />
      </div>

      {/* Controls bar */}
      <VideoControlsBar
        micOn={micOn}
        camOn={camOn}
        onToggleMic={() => setMicOn((v) => !v)}
        onToggleCam={() => setCamOn((v) => !v)}
        onHangUp={hangUp}
        endLoading={endLoading}
        isDoctor={isDoctor}
        rxOpen={rxOpen}
        onOpenRx={() => setRxOpen(true)}
        transcriptOpen={transcriptOpen}
        onToggleTranscript={() => setTranscriptOpen((v) => !v)}
        notesOpen={notesOpen}
        onToggleNotes={() => setNotesOpen((v) => !v)}
      />

      {/* Clinical notes overlay */}
      <AnimatePresence>
        {notesOpen && isDoctor && (
          <NotesPanel apptId={appt_id} onClose={() => setNotesOpen(false)} />
        )}
      </AnimatePresence>

      {/* Live transcript panel (doctor-only) */}
      <AnimatePresence>
        {transcriptOpen && isDoctor && (
          <LiveTranscriptPanel apptId={appt_id} speakerRole="doctor" onClose={() => setTranscriptOpen(false)} />
        )}
      </AnimatePresence>

      {/* Prescription dialog */}
      {isDoctor && (
        <PrescriptionDialog
          patientId={appt?.patient_id}
          appointmentId={appt_id}
          open={rxOpen}
          onOpenChange={setRxOpen}
        />
      )}
    </div>
  );
};

export default VideoRoomPage;
